import { fetchHeardAboutUsOptions } from "app/api/registrationApi";
import InputWithLabel from "app/components/FormInputs/components/InputWithLabel/InputWithLabel";
import { registrationInputMaxLength } from "app/utils/const";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

/**
 * Component that shows a label and a select with a
 * list of heardAboutUs options.
 * When selected the option's name is passed up to AccountRegistration component
 * Unless selected option is "Other", then show an input and pass the input value up as the selected "heard about us" option
 */

type Option = {
  id: number;
  name: string;
};

type Props = {
  handleHeardAboutUsChange: Function;
  heardAboutUs: string;
  handleChange: Function;
  heardAboutUsOther: string;
};

const HeardAboutUs = (props: Props) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedOption, setSelectedOption] = useState<Option>();
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [isOtherInputActive, setIsOtherInputActive] = useState(false);
  const maxLength = registrationInputMaxLength.otherReason;
  const [remainingCharacters, setRemainingCharacters] = useState(maxLength);
  const inputRef = useRef(null);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchHeardAboutUsOptions();
        // Add an "Other" option, use -1 as id to prevent clashes with api ids
        setOptions([
          ...response.options,
          {
            id: -1,
            name: (
              <FormattedMessage
                id="register-heardaboutusoptions-other"
                defaultMessage="Other"
              />
            ),
            active: true,
          },
        ]);
      } catch (error) {
        console.error("Error fetching options:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    // Check if input has focus
    const trackFocus = () => {
      if (inputRef.current === document.activeElement) {
        setIsOtherInputActive(true);
      } else {
        setIsOtherInputActive(false);
      }
    };
    document.addEventListener("focusin", trackFocus);
    document.addEventListener("focusout", trackFocus);
    return () => {
      document.removeEventListener("focusin", trackFocus);
      document.removeEventListener("focusout", trackFocus);
    };
  }, []);

  useEffect(() => {
    const currentLength = props.heardAboutUsOther.length;
    currentLength && setRemainingCharacters(maxLength - currentLength);
  }, [props.heardAboutUsOther, maxLength]);

  const handleSelectChange = e => {
    // When the select changes, reset the entered Other reason
    props.handleChange("heardAboutUsOther", "");
    if (+e.currentTarget.value === -1) {
      //Selected option is "Other"
      setIsOtherSelected(true);
      const selectedOption = options.find(
        option => option.id === +e.currentTarget.value
      );
      selectedOption && setSelectedOption(selectedOption);
      // Special condition when Other is selected
      // User must enter a string
      // This string is sent to api with format "Other - {USER_REASON}"
      selectedOption && props.handleHeardAboutUsChange("other");
    } else {
      setIsOtherSelected(false);
      const selectedOption = options.find(
        option => option.id === +e.currentTarget.value
      );
      selectedOption && setSelectedOption(selectedOption);
      // Set selected option name in the Account registration component
      selectedOption && props.handleHeardAboutUsChange(selectedOption.name);
    }
  };

  return (
    <>
      <div className="register-screen__form__dropdown">
        <label className="text-input__label" htmlFor="selectComponent">
          <FormattedMessage tagName="span" id="hear-about-3ET" />
          {"*"}
        </label>
        <select
          className="form-control"
          id="selectComponent"
          disabled={isLoading}
          onChange={handleSelectChange}
        >
          {!selectedOption && (
            <option value={0}>
              <FormattedMessage id="home-register-select-heard-about-us" />
            </option>
          )}
          {options &&
            options.map((option, index) => (
              <option key={index} value={option.id}>
                {option.name}
              </option>
            ))}
        </select>
      </div>

      <InputWithLabel
        label={
          <FormattedMessage
            id="register-heardaboutus-specify"
            defaultMessage="Please specify"
          />
        }
        inputId="heardAboutUsOther"
        value={props.heardAboutUsOther || ""}
        onValueChange={props.handleChange}
        required={true}
        disabled={!isOtherSelected}
        maxLength={registrationInputMaxLength.otherReason}
        inputRef={inputRef}
      />
      {/* Show help message when input is active and user has entered something */}
      {isOtherInputActive && props.heardAboutUsOther && (
        <span className="register-screen__form__other-message">
          <FormattedMessage
            id="register-heardaboutus-characters-remaining"
            defaultMessage={"Characters remaining"}
          />
          : {remainingCharacters}{" "}
        </span>
      )}
    </>
  );
};

export default HeardAboutUs;
